<script>
import { showModal } from "@app/store/modalStore.svelte.js";
import { commitHistory } from "@app/store/historyStore.svelte.js";
import { projectState } from "@app/store/projectStore.svelte.js";
import { editorState } from "@app/store/editorStore.svelte.js";
import { eventGroupState, isSelectedEventGroupLocked } from "@app/store/eventGroupStore.svelte.js";
import { toast } from "@zerodevx/svelte-toast";
import { authState } from "@app/store/authStore.svelte.js";
import { environment } from "@app/store/envStore.svelte.js";
import UndoRedo from "./UndoRedo.svelte";
import Menu from "../menu/Menu.svelte";
import MenuItem from "../menu/MenuItem.svelte";
import MenuDivider from "../menu/MenuDivider.svelte";
import autoFormat from "@cclib/functions/eventGroups/autoFormat.js";
import insertEvent from "@cclib/functions/eventGroups/insertEvent.js";
import removeEvent from "@cclib/functions/eventGroups/removeEvent.js";
import stripTags from "@cclib/functions/quill/stripTags.js";
import splitIntoParagraphs from "@cclib/functions/quill/splitIntoParagraphs.js";
import { current } from "immer";
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import * as m from "@app/paraglide/messages.js";
import { getCopyText, getHighlightedDomText, getQuillTextSelection } from "@app/lib/copySelection.js";

let isLocked = $derived(isSelectedEventGroupLocked());

function guardedShowModal(modalName) {
    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }
    showModal(modalName);
}

function isBlankParagraph(paragraph) {
    const inner = paragraph.replace(/^<p\b[^>]*>/i, "").replace(/<\/p>$/i, "");
    return inner.replace(/<br\s*\/?>/gi, "").trim() === "";
}

function cleanEventText(html) {
    let text = html
        .replace(/<p([^>]*)>\s+/g, "<p$1>")
        .replace(/\s+<\/p>/g, "</p>");

    const paragraphs = splitIntoParagraphs(text);
    if (!paragraphs) return text;

    return paragraphs.filter((p) => !isBlankParagraph(p)).join("");
}

async function removeLeadingSpaces() {
    if (!eventGroupState[projectState.selected]) {
        return;
    }

    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    const egIdx = projectState.selected;

    commitHistory({
        name: "remove leading spaces",
        eventGroup: egIdx,
        mutate: {
            eventGroupState: (draft) => {
                draft[egIdx].selected = [];
                draft[egIdx].events.forEach((event, index, events) => {
                    events[index].text = cleanEventText(event.text);
                });
            },
        },
    });
}

async function removeEmptyEvents(group) {
    if (!eventGroupState[group]) {
        return;
    }

    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    const egIdx = projectState.selected;

    commitHistory({
        name: "remove events",
        eventGroup: egIdx,
        mutate: {
            eventGroupState: (draft) => {
                draft[egIdx].selected = [];
                draft[group].events = draft[group].events.filter((event) => {
                    return stripTags(event.text) !== "";
                });
            },
        },
    });
}

function pasteEvents() {
    try {
        if (!window.quillEditor) {
            return;
        }

        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        navigator.clipboard.readText().then((text) => {
            try {
                let clipboardEvents = JSON.parse(text);
                if (
                    clipboardEvents.length > 0 &&
                    clipboardEvents[0].id &&
                    clipboardEvents[0].style
                ) {
                    let currentTime = player ? player.currentTime : 0;
                    Swal.fire({
                        ...SWAL_BASE,
                        customClass: SWAL_CLASSES.confirmCancel,
                        title: m.paste_events(),
                        text: m.paste_events_confirm(),
                        icon: "question",
                        showCancelButton: true,
                        confirmButtonText: m.yes(),
                        cancelButtonText: m.use_original_timing(),
                    })
                        .then((sAlertRes) => {
                            const egIdx = projectState.selected;
                            const baseSelected =
                                eventGroupState[egIdx].selected[0] ?? -1;
                            let newCurrentTime = currentTime;

                            commitHistory({
                                name: "paste events",
                                eventGroup: egIdx,
                                mutate: {
                                    eventGroupState: (draft) => {
                                        clipboardEvents.forEach(
                                            (event, count, events) => {
                                                if (
                                                    sAlertRes.isConfirmed &&
                                                    currentTime >= 0
                                                ) {
                                                    let duration =
                                                        event.end - event.start;
                                                    let startTime =
                                                        count === 0
                                                            ? currentTime
                                                            : currentTime +
                                                              (event.start -
                                                                  events[0]
                                                                      .end);

                                                    startTime = startTime
                                                        ? startTime
                                                        : newCurrentTime;
                                                    let endTime =
                                                        startTime + duration;
                                                    newCurrentTime = endTime;
                                                    draft[egIdx] = insertEvent(
                                                        current(draft[egIdx]),
                                                        {
                                                            ...event,
                                                            start: startTime,
                                                            end: endTime,
                                                        },
                                                        baseSelected +
                                                            count +
                                                            1,
                                                    );
                                                } else {
                                                    draft[egIdx] = insertEvent(
                                                        current(draft[egIdx]),
                                                        event,
                                                        baseSelected +
                                                            count +
                                                            1,
                                                    );
                                                }
                                            },
                                        );
                                    },
                                },
                            });
                        })
                        .catch((err) => {
                            console.log("Error pasting events");
                            console.log(err);
                            console.log(err.message);
                        });
                } else {
                    throw new Error("No events found in clipboard");
                }
            } catch (err) {
                const egIdx = projectState.selected;
                const selectedIdx = eventGroupState[egIdx]?.selected[0];
                let range = window?.quillEditor?.getSelection();
                if (!range || selectedIdx === undefined) {
                    return;
                }

                if (range.length === 0) {
                    window.quillEditor.insertText(range.index, text, "silent");
                } else {
                    window.quillEditor.deleteText(
                        range.index,
                        range.length,
                        "silent",
                    );
                    window.quillEditor.insertText(range.index, text, "silent");
                }

                const pastedText = window.quillEditor.root.innerHTML;
                const shouldAutoFormat = editorState.autoFormatText;
                const maxLines = eventGroupState[egIdx]
                    ? eventGroupState[egIdx].maxLines
                    : 2;
                const maxChars = eventGroupState[egIdx]
                    ? eventGroupState[egIdx].maxChars
                    : 32;

                commitHistory({
                    name: "paste events",
                    eventGroup: egIdx,
                    mutate: {
                        eventGroupState: (draft) => {
                            draft[egIdx].events[selectedIdx].text = pastedText;
                            if (shouldAutoFormat) {
                                draft[egIdx] = autoFormat(
                                    current(draft[egIdx]),
                                    maxLines,
                                    maxChars,
                                    0,
                                    true,
                                    [selectedIdx],
                                );
                                draft[egIdx].selected = [selectedIdx];
                            }
                        },
                    },
                });

                if (shouldAutoFormat && window.quillEditor) {
                    const ev = eventGroupState[egIdx].events[selectedIdx];
                    if (ev) {
                        let delta = window.quillEditor.clipboard.convert({
                            html: ev.text,
                        });
                        window.quillEditor.setContents(delta, "silent");
                        window.quillEditor.formatLine(
                            0,
                            9999,
                            "align",
                            ev.alignment === "left" ? "" : ev.alignment,
                            "silent",
                        );
                    }
                }
            }
        });
    } catch (err) {
        console.log(err.message);
    }
}

async function copyEvents() {
    const textToCopy = getCopyText({
        eventGroup: eventGroupState[projectState.selected],
    });
    if (textToCopy == null) {
        return;
    }

    navigator.clipboard.writeText(textToCopy);
}

async function cutEvents() {
    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    const eventGroup = eventGroupState[projectState.selected];
    if (!eventGroup?.selected?.length) {
        return;
    }

    const quillSelection = getQuillTextSelection(eventGroup);
    const egIdx = projectState.selected;

    if (quillSelection) {
        const selectedIdx = eventGroup.selected[0];
        quillSelection.editor.deleteText(
            quillSelection.range.index,
            quillSelection.range.length,
            "silent",
        );
        const newText = quillSelection.editor.root.innerHTML;

        commitHistory({
            name: "cut event(s)",
            eventGroup: egIdx,
            mutate: {
                eventGroupState: (draft) => {
                    draft[egIdx].events[selectedIdx].text = newText;
                },
            },
        });
        navigator.clipboard.writeText(quillSelection.text);
        return;
    }

    const domText = getHighlightedDomText();
    if (domText) {
        navigator.clipboard.writeText(domText);
        return;
    }

    let eventsToCut = [];
    let selectedEvents = [...eventGroup.selected].sort((a, b) => b - a);

    selectedEvents.forEach((eventIndex) => {
        eventsToCut.push(eventGroup.events[eventIndex]);
    });

    commitHistory({
        name: "cut event(s)",
        eventGroup: egIdx,
        mutate: {
            eventGroupState: (draft) => {
                draft[egIdx].selected = [];
                selectedEvents.forEach((eventIndex) => {
                    draft[egIdx] = removeEvent(
                        current(draft[egIdx]),
                        eventIndex,
                    );
                });
            },
        },
    });

    navigator.clipboard.writeText(JSON.stringify(eventsToCut));
}
</script>

<Menu id="edit" label={m.edit()}>
    <UndoRedo />
    <MenuDivider />
    <MenuItem
        label={m.cut()}
        icon="bi-scissors"
        id="edit-cut"
        shortcut="Cut"
        disabled={isLocked}
        disabledReason={m.event_group_locked_toast()}
        onSelect={cutEvents} />
    <MenuItem
        label={m.copy()}
        icon="bi-clipboard-plus"
        id="edit-copy"
        shortcut="Copy"
        onSelect={copyEvents} />
    <MenuItem
        label={m.paste()}
        icon="bi-clipboard-check"
        id="edit-paste"
        shortcut="Paste"
        disabled={isLocked}
        disabledReason={m.event_group_locked_toast()}
        onSelect={pasteEvents} />
    <MenuDivider />
    <MenuItem
        label={m.remove_empty_events()}
        icon="bi-trash"
        id="edit-remove-empty"
        disabled={isLocked}
        disabledReason={m.event_group_locked_toast()}
        onSelect={() => removeEmptyEvents(projectState.selected)} />
    <MenuItem
        label={m.remove_sdh()}
        icon="bi-volume-mute"
        id="edit-remove-sdh"
        disabled={isLocked}
        disabledReason={m.event_group_locked_toast()}
        onSelect={() => guardedShowModal("removeSdh")} />
    <MenuItem
        label={m.remove_leading_spaces()}
        icon="bi-text-indent-left"
        id="edit-remove-spaces"
        disabled={isLocked}
        disabledReason={m.event_group_locked_toast()}
        onSelect={removeLeadingSpaces} />
    <MenuDivider />
    <MenuItem
        label={m.go_to_event()}
        icon="bi-box-arrow-in-right"
        id="edit-goto"
        shortcut="Go To Event"
        disabled={!eventGroupState[projectState.selected]}
        onSelect={() => showModal("goToEvent")} />
    {#if !environment.iframe}
        <MenuDivider />
        {#if authState.team && environment.online}
            <MenuItem
                label={m.knp_manager()}
                icon="bi-journal-text"
                id="edit-knp-manager"
                onSelect={() => showModal("knpManager")} />
        {/if}
        <MenuItem
            label={m.auto_replacement()}
            icon="bi-arrow-repeat"
            id="edit-auto-replace"
            disabled={isLocked}
            disabledReason={m.event_group_locked_toast()}
            onSelect={() => guardedShowModal("autoReplace")} />
        <MenuItem
            label={m.export_presets()}
            icon="bi-upload"
            id="edit-preset-manager"
            onSelect={() => showModal("presetManager")} />
        <MenuItem
            label={m.shortcut_keys()}
            icon="bi-keyboard"
            id="edit-shortcuts"
            onSelect={() => showModal("shortcutKeys")} />
        <MenuItem
            label={m.options()}
            icon="bi-sliders"
            id="edit-options"
            onSelect={() => showModal("options")} />
    {/if}
</Menu>
